import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from '@ais-ui/ui-core';
import { useLayers } from '@ais-ui/react-openlayers';
import { FeatureLike } from 'ol/Feature';
import React, { useState } from 'react';
import { usePopper } from 'react-popper';

interface VectorTilePopoverProps {
  feature: FeatureLike;
  position: { x: number; y: number } | null;
}

const VectorTilePopover = ({ feature, position }: VectorTilePopoverProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const properties = feature.getProperties();
  const name = properties.name || 'Infrastructure Details';

  const formattedProperties = Object.entries(properties)
    .filter(
      ([key]) => !key.startsWith('_') && key !== 'geometry' && key !== 'name',
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const [virtualReference] = useState(() => ({
    getBoundingClientRect: () =>
      ({
        top: position ? position.y : 0,
        left: position ? position.x : 0,
        bottom: position ? position.y : 0,
        right: position ? position.x : 0,
        width: 0,
        height: 0,
      }) as DOMRect,
  }));

  const { styles } = usePopper(virtualReference, popperElement, {
    strategy: 'fixed',
    placement: 'right',
  });

  if (!feature || !position) return null;
  return (
    <Card
      ref={setPopperElement}
      style={styles.popper}
      className="mx-6 p-0 min-w-[128px]"
    >
      <CardHeader className="p-3">
        <CardTitle className="text-sm">{name}</CardTitle>
      </CardHeader>

      <CardContent className="pt-0 space-y-2 px-3">
        <Separator></Separator>
        <div className="space-y-1">
          {Object.entries(formattedProperties).map(([key, value]) => (
            <div key={key} className="grid grid-cols-2 gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {key}:
              </span>
              <span className="text-sm">{String(value)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const VectorTileHover = () => {
  const { vectorTileHoverState } = useLayers();

  if (!vectorTileHoverState.feature || !vectorTileHoverState.position) {
    return null;
  }

  return (
    <VectorTilePopover
      feature={vectorTileHoverState.feature}
      position={vectorTileHoverState.position}
    />
  );
};
